<template>
  <div class="hstack">
    <span
      v-if="text"
      v-html="text"
    >
    </span>
    <span v-else-if="up">—</span>
    <button
      type="button"
      class="btn-sm btn"
      @click.stop="editing = true"
      :disabled="editing"
      v-if="canEdit"
    >
      <i class="bi bi-pencil"></i>
    </button>
  </div>

  <Mod
    v-if="editing"
    size="fit-content"
    ref="mod"
    @hidden="editing = false"
  >
    <Blockscreen v-if="loading" />
    <form
      @submit.prevent="submit"
      :id="`form-${id}`"
      class="hstack gap-3"
    >
      <div class="vstack">
        <label>{{ $t('actNumber') }}</label>
        <input
          type="text"
          class="form-control form-control-sm"
          :placeholder="$t('number')"
          v-model.number="editmodel.num_wo"
        />
      </div>
      <div class="vstack">
        <label>{{ $t('actDate') }}</label>
        <Datepicker
          :placeholder="$t('date')"
          v-model="editmodel.date_wo"
        />
      </div>
    </form>

    <template #header
      >{{ $t('inventoryWriteOffActFor') }}&nbsp;
      <span v-if="destructionId"> {{ $t('destruction', { id: destructionId }) }} </span>
      <span v-else-if="orderId"> {{ $t('order', { id: orderId }) }} </span>
      <span v-else-if="writeoffId"> {{ $t('writeoffNum', { id: writeoffId }) }} </span>
    </template>
    <template #footer>
      <button
        type="submit"
        class="btn btn-primary"
        :form="`form-${id}`"
        :disabled="loading"
      >
        <span
          class="spinner-border spinner-border-sm"
          aria-hidden="true"
          v-if="loading"
        ></span>
        <i
          class="bi bi-check"
          v-else
        ></i>
        {{ $t('common.save') }}
      </button>
    </template>
  </Mod>
</template>
<script setup>
import { computed, inject, ref, toValue, useId, useTemplateRef, watch } from 'vue'
import { commons } from '@/lib/transformer.js'
import { useApi } from '@/composables/api.js'
import Datepicker from '@/components/datepicker.vue'
import Mod from '@/components/mod.vue'
import Blockscreen from '@/components/blockscreen.vue'

const id = useId()
const division = 'writeoff_signed'

defineEmits(['updated'])
const props = defineProps({
  row: {
    type: Array,
    default: () => [],
  },
  orderId: {
    type: [Number, String],
    required: false,
  },
  destructionId: {
    type: [Number, String],
    required: false,
  },
  writeoffId: {
    type: [Number, String],
    required: false,
  },
  canEdit: {
    type: Boolean,
    default: false,
  },
  uploadIndex: {
    type: Number,
    default: 0,
  },
})
const model = ref({})
const editmodel = ref({})
const editing = ref(false)
const loading = ref(false)
const elMod = useTemplateRef('mod')
const refreshList = inject('refresh')

// const { lock, reset } = useLock('orders', props.orderId)
const updateUrl = computed(() =>
  up.value?.secure_filename ? `upload/${up.value.secure_filename}` : ''
)
const { refresh } = useApi({
  url: updateUrl,
  method: 'PUT',
})

const text = computed(() => {
  const parts = []
  if (model.value?.num_wo) parts.push(`№${model.value.num_wo}`)
  if (model.value?.date_wo) parts.push(commons.datestring(model.value.date_wo))
  if (!parts.length) return null
  return parts.join('<br/>')
})
const up = computed(() => (props.row || []).filter(u => u.division == division)[props.uploadIndex])

const canEdit = computed(() => props.canEdit && up.value)

watch(editing, editing => {
  editmodel.value = editing ? { ...model.value } : {}
})
watch(
  up,
  up => {
    if (!up) {
      model.value = undefined
      return
    }
    model.value = { ...up.metadata }
  },
  { immediate: true }
)

async function submit() {
  if (!up.value?.secure_filename) return

  const metadata = {
    ...(up.value.metadata || {}),
    ...toValue(editmodel),
  }

  try {
    loading.value = true
    await refresh({ data: { metadata } })
    await refreshList()
    elMod.value.hide()
  } finally {
    loading.value = false
  }
}
</script>

<i18n>
{
  "ru": {
    "actNumber": "Номер акта",
    "number": "Номер",
    "actDate": "Дата акта",
    "date": "Дата",
    "inventoryWriteOffActFor": "Акт списания ТМЦ к",
    "destruction": "уничтожению № {id}",
    "order": "заявке № {id}",
    "writeoffNum": "№ {id}"
  },
  "en": {
    "actNumber": "Act number",
    "number": "Number",
    "actDate": "Act date",
    "date": "Date",
    "inventoryWriteOffActFor": "Inventory write-off act for",
    "destruction": "destruction request No. {id}",
    "order": "order No. {id}",
    "writeoffNum": "No. {id}"
  }
}
</i18n>
